import React from "react";

interface FlagIconProps {
  country?: string;
  code?: string;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

// Crisp inline SVGs for all primary country offices
export function GermanyFlag({ className = "w-12 h-8 rounded-md shadow-sm" }: { className?: string }) {
  return (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#000000" d="M0 0h640v160H0z" />
      <path fill="#DD0000" d="M0 160h640v160H0z" />
      <path fill="#FFCE00" d="M0 320h640v160H0z" />
    </svg>
  );
}

export function USAFlag({ className = "w-12 h-8 rounded-md shadow-sm" }: { className?: string }) {
  return (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <rect width="640" height="480" fill="#bd3d44" />
      <path stroke="#fff" strokeWidth="37" d="M0 55.4h640M0 129.2h640M0 203h640M0 277h640M0 350.8h640M0 424.6h640" />
      <rect width="260" height="258.5" fill="#192f5d" />
      {/* 50 Star Grid representation */}
      <g fill="#fff">
        {[...Array(5)].map((_, r) =>
          [...Array(6)].map((_, c) => (
            <circle key={`star-a-${r}-${c}`} cx={24 + c * 42} cy={26 + r * 50} r="6" />
          ))
        )}
        {[...Array(4)].map((_, r) =>
          [...Array(5)].map((_, c) => (
            <circle key={`star-b-${r}-${c}`} cx={45 + c * 42} cy={51 + r * 50} r="6" />
          ))
        )}
      </g>
    </svg>
  );
}

export function CanadaFlag({ className = "w-12 h-8 rounded-md shadow-sm" }: { className?: string }) {
  return (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#FF0000" d="M0 0h160v480H0zm480 0h160v480H480z" />
      <path fill="#FFFFFF" d="M160 0h320v480H160z" />
      {/* Maple Leaf vector */}
      <path
        fill="#FF0000"
        d="M320 90l18 48 35-12-10 38 42 12-25 32 38 22-48 18 12 40-42-18-20 60-20-60-42 18 12-40-48-18 38-22-25-32 42-12-10-38 35 12z"
      />
    </svg>
  );
}

export function UKFlag({ className = "w-12 h-8 rounded-md shadow-sm" }: { className?: string }) {
  return (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <clipPath id="uk-clip">
        <path d="M0 0h640v480H0z" />
      </clipPath>
      <g clipPath="url(#uk-clip)">
        <path fill="#012169" d="M0 0h640v480H0z" />
        <path stroke="#fff" strokeWidth="60" d="M0 0l640 480M640 0L0 480" />
        <path stroke="#C8102E" strokeWidth="40" d="M0 0l640 480M640 0L0 480" />
        <path stroke="#fff" strokeWidth="100" d="M320 0v480M0 240h640" />
        <path stroke="#C8102E" strokeWidth="60" d="M320 0v480M0 240h640" />
      </g>
    </svg>
  );
}

export function NigeriaFlag({ className = "w-12 h-8 rounded-md shadow-sm" }: { className?: string }) {
  return (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#008751" d="M0 0h213.3v480H0z" />
      <path fill="#FFFFFF" d="M213.3 0h213.4v480H213.3z" />
      <path fill="#008751" d="M426.7 0h213.3v480H426.7z" />
    </svg>
  );
}

export function FlagIcon({ country = "", code = "", className = "", size = "md" }: FlagIconProps) {
  const normalized = (code || country || "").toLowerCase().trim();

  const sizeClasses = {
    sm: "w-8 h-6 text-xl",
    md: "w-12 h-8 text-3xl",
    lg: "w-16 h-11 text-4xl",
    xl: "w-20 h-14 text-5xl",
  }[size];

  const combinedClass = `${sizeClasses} ${className} inline-block object-contain rounded-md overflow-hidden shadow-sm`;

  if (
    normalized.includes("de") ||
    normalized.includes("germany") ||
    normalized.includes("deutschland") ||
    country.includes("🇩🇪")
  ) {
    return <GermanyFlag className={combinedClass} />;
  }

  if (
    normalized.includes("us") ||
    normalized.includes("usa") ||
    normalized.includes("united states") ||
    normalized.includes("america") ||
    country.includes("🇺🇸")
  ) {
    return <USAFlag className={combinedClass} />;
  }

  if (
    normalized.includes("ca") ||
    normalized.includes("canada") ||
    country.includes("🇨🇦")
  ) {
    return <CanadaFlag className={combinedClass} />;
  }

  if (
    normalized.includes("gb") ||
    normalized.includes("uk") ||
    normalized.includes("united kingdom") ||
    normalized.includes("britain") ||
    normalized.includes("england") ||
    country.includes("🇬🇧")
  ) {
    return <UKFlag className={combinedClass} />;
  }

  if (
    normalized.includes("ng") ||
    normalized.includes("nigeria") ||
    country.includes("🇳🇬")
  ) {
    return <NigeriaFlag className={combinedClass} />;
  }

  // Fallback for any other country: flagcdn image
  const twoLetterCode = (code || country.slice(0, 2)).toLowerCase().replace(/[^a-z]/g, "");
  if (twoLetterCode.length === 2) {
    return (
      <img
        src={`https://flagcdn.com/w80/${twoLetterCode}.png`}
        alt={`${country} flag`}
        className={combinedClass}
        loading="lazy"
        onError={(e) => {
          // If network image fails, fallback to styled country pill
          (e.target as HTMLElement).style.display = "none";
        }}
      />
    );
  }

  return (
    <span className={`${sizeClasses} ${className} inline-flex items-center justify-center font-bold text-xs bg-primary/20 text-primary rounded`}>
      {code || country.slice(0, 2).toUpperCase()}
    </span>
  );
}
